import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { motion } from "framer-motion";

const CaseStudies = () => {
  const caseStudyButton = "w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-full h-12 font-semibold shadow-md hover:shadow-lg transition-all";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50/30 to-white">
      <SEO
        title="Startup Validation Case Studies - Learn from Success Stories"
        description="See how Dropbox, Airbnb, Product Hunt, and other successful startups validated their ideas before building. Real examples of idea validation in action."
        keywords="startup validation case studies, Dropbox validation, Airbnb validation, Product Hunt validation, startup success stories"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-indigo-50/30 to-white">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900">
                Validation Success Stories
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Learn how tech giants started with simple validation tests before building their full products
              </p>

              {/* Dashboard image */}
              <div className="mt-12 max-w-5xl mx-auto">
                <motion.img
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  src="/images/dashboard.jpg"
                  alt="WillTheyConvert Dashboard"
                  className="w-full h-auto rounded-3xl shadow-2xl border border-gray-200"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16">
        <div className="container max-w-5xl mx-auto px-6">
          {/* Dropbox Case Study */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-3 flex items-center justify-center shadow-md">
                <img src="/images/dropbox.webp" alt="Dropbox" className="w-full h-full object-contain filter brightness-0 invert" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Dropbox</h2>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Challenge</h3>
                  <p className="text-gray-600 leading-relaxed">
                    In 2007, Drew Houston had an idea for seamless file synchronization across devices.
                    Building it would take months of complex development. But would people actually use it?
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Validation Strategy</h3>
                  <p className="text-gray-600 leading-relaxed mb-5">
                    Instead of building the product first, Drew created a simple 3-minute demo video explaining how Dropbox would work.
                    The video was targeted at tech-savvy early adopters on Hacker News and Reddit.
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Created a simple explainer video showing the product in action</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Added an email signup form for beta access</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Posted on communities where target users hung out</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Results</h3>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200 mb-5">
                    <p className="text-green-800 font-semibold text-lg">75,000 email signups overnight</p>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    The overnight success validated the market demand and gave Drew confidence to focus on building the product.
                    Today, Dropbox is worth over $8 billion and has millions of users worldwide.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">Key Takeaway</h3>
                  <p className="text-gray-600 leading-relaxed">
                    You don't need a finished product to validate demand. A simple demo video and signup form can prove if people want your solution.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-violet-50 p-8 rounded-2xl border border-indigo-200">
                  <h3 className="text-lg md:text-xl font-bold mb-4 text-indigo-900">
                    How to do this with WillTheyConvert
                  </h3>
                  <p className="text-gray-700 mb-5 leading-relaxed">
                    Create a waitlist flow to collect email addresses from potential customers before building your product:
                  </p>
                  <ul className="space-y-4 mb-6">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Use our <strong>Waitlist Flow</strong> template to create a landing page with email collection</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Add a product description and mock screenshots to explain your concept</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Track signup metrics to gauge interest before building</span>
                    </li>
                  </ul>
                  <Link to="/create">
                    <Button className={caseStudyButton}>
                      Create Your Waitlist Flow
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Airbnb Case Study */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 p-3 flex items-center justify-center shadow-md">
                <img src="/images/airbnb.webp" alt="Airbnb" className="w-full h-full object-contain filter brightness-0 invert" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Airbnb</h2>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Challenge</h3>
                  <p className="text-gray-600 leading-relaxed">
                    In 2007, Brian Chesky and Joe Gebbia had a radical idea: people might pay to stay in strangers' homes.
                    But would anyone actually feel comfortable doing this?
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Validation Strategy</h3>
                  <p className="text-gray-600 leading-relaxed mb-5">
                    They started with a simple WordPress site featuring their own apartment during a design conference in San Francisco.
                    No fancy platform, just photos and basic booking via email.
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Created a basic website with photos of their apartment</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Offered air mattresses and breakfast for $80/night</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Targeted design conference attendees needing accommodation</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Results</h3>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200 mb-5">
                    <p className="text-green-800 font-semibold text-lg">3 guests booked immediately, proving people would trust strangers</p>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    This simple test proved people would trust strangers and pay to stay in their homes.
                    Today, Airbnb is worth over $75 billion and has revolutionized travel accommodation.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">Key Takeaway</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Start small and test your riskiest assumption first. Airbnb proved trust was possible before building their platform.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-violet-50 p-8 rounded-2xl border border-indigo-200">
                  <h3 className="text-lg md:text-xl font-bold mb-4 text-indigo-900">
                    How to do this with WillTheyConvert
                  </h3>
                  <p className="text-gray-700 mb-5 leading-relaxed">
                    Test if people would pay for your service without building the actual platform:
                  </p>
                  <ul className="space-y-4 mb-6">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Use our <strong>Payment Simulation Flow</strong> to create a realistic booking experience</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Add photos and descriptions of what you're offering</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Analyze how many visitors attempt to complete a purchase</span>
                    </li>
                  </ul>
                  <Link to="/create">
                    <Button className={caseStudyButton}>
                      Create Your Payment Simulation
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Product Hunt Case Study */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 p-3 flex items-center justify-center shadow-md">
                <span className="text-3xl font-black text-white">P</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Product Hunt</h2>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Challenge</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Ryan Hoover wanted to create a community for discovering new products.
                    Building a full platform would take months, but would people even want daily product discoveries?
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Validation Strategy</h3>
                  <p className="text-gray-600 leading-relaxed mb-5">
                    Instead of building a complex platform, Ryan started with a simple email list using Linky (now Linkydink).
                    He invited 30 friends to submit and receive daily product discoveries.
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Created an email list for sharing product discoveries</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Invited tech enthusiasts to contribute and subscribe</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Built the community before the platform</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Results</h3>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200 mb-5">
                    <p className="text-green-800 font-semibold text-lg">The email list grew rapidly, validating the community concept</p>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    The email list's success proved people wanted a place to discover products.
                    Only then did Ryan build the Product Hunt platform, which was later acquired by AngelList.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">Key Takeaway</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Start with the simplest possible version of your idea. An email list can validate a community concept.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-violet-50 p-8 rounded-2xl border border-indigo-200">
                  <h3 className="text-lg md:text-xl font-bold mb-4 text-indigo-900">
                    How to do this with WillTheyConvert
                  </h3>
                  <p className="text-gray-700 mb-5 leading-relaxed">
                    Build an engaged community before investing in platform development:
                  </p>
                  <ul className="space-y-4 mb-6">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Use our <strong>Newsletter Flow</strong> to collect subscribers interested in your niche</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Test different value propositions to see what resonates most</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Use our analytics to measure open rates and engagement</span>
                    </li>
                  </ul>
                  <Link to="/create">
                    <Button className={caseStudyButton}>
                      Create Your Newsletter Flow
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Groupon Case Study */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 p-3 flex items-center justify-center shadow-md">
                <img src="/images/groupon.webp" alt="Groupon" className="w-full h-full object-contain filter brightness-0 invert" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Groupon</h2>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Challenge</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Andrew Mason wanted to create a platform for collective buying power.
                    But would businesses offer deep discounts, and would customers buy together?
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Validation Strategy</h3>
                  <p className="text-gray-600 leading-relaxed mb-5">
                    Instead of building a complex marketplace, they started with a simple WordPress blog.
                    They manually posted one deal a day and used PDFs for coupons.
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Created a WordPress blog for daily deals</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Manually coordinated with local businesses</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">Used basic tools like PayPal and PDF coupons</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">The Results</h3>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200 mb-5">
                    <p className="text-green-800 font-semibold text-lg">The first deal sold hundreds of pizzas, proving the concept worked</p>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    This simple test proved both businesses and customers would participate in group buying.
                    Groupon went on to become one of the fastest-growing companies in history.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">Key Takeaway</h3>
                  <p className="text-gray-600 leading-relaxed">
                    You don't need complex technology to test a business model. A WordPress blog and manual processes can validate your concept.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-violet-50 p-8 rounded-2xl border border-indigo-200">
                  <h3 className="text-lg md:text-xl font-bold mb-4 text-indigo-900">
                    How to do this with WillTheyConvert
                  </h3>
                  <p className="text-gray-700 mb-5 leading-relaxed">
                    Test if your discount or group buying model will attract customers:
                  </p>
                  <ul className="space-y-4 mb-6">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Use our <strong>Discount Test Flow</strong> to see if price incentives drive conversions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Try our <strong>Referral Program Flow</strong> to test viral growth potential</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-700">Analyze which offers generate the most interest before building your platform</span>
                    </li>
                  </ul>
                  <Link to="/create">
                    <Button className={caseStudyButton}>
                      Create Your Discount Test
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center py-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Ready to Validate Your Idea?</h2>
            <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
              Follow in the footsteps of these successful companies. Test your idea with real users before investing in development.
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
              <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Test Payment Willingness</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Create a realistic checkout experience without processing actual payments
                </p>
                <Link to="/create">
                  <Button variant="outline" className="w-full rounded-full h-11 border-2 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 font-semibold">
                    Payment Simulation
                  </Button>
                </Link>
              </div>

              <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Build a Waitlist</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Collect email addresses from interested customers before launch
                </p>
                <Link to="/create">
                  <Button variant="outline" className="w-full rounded-full h-11 border-2 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 font-semibold">
                    Waitlist Flow
                  </Button>
                </Link>
              </div>

              <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Test Pricing Models</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Compare different pricing tiers to find the optimal price point
                </p>
                <Link to="/create">
                  <Button variant="outline" className="w-full rounded-full h-11 border-2 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 font-semibold">
                    Pricing Table Flow
                  </Button>
                </Link>
              </div>

              <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Test Viral Growth</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  See if your idea has potential for referral-based growth
                </p>
                <Link to="/create">
                  <Button variant="outline" className="w-full rounded-full h-11 border-2 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 font-semibold">
                    Referral Program
                  </Button>
                </Link>
              </div>
            </div>

            <Link to="/create">
              <Button size="lg" className="gap-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-10 h-14 text-lg font-semibold">
                Start Testing Your Idea Now <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
